import { GpmPayConfigError } from '../../core/errors.js';
import type { BankAccount } from '../../types/bank.js';
import type { BankAccountStatus } from '../../types/enums.js';
import { enumArg, intArg, requireArg } from '../args.js';
import { clientFromFlags, type GlobalFlags } from '../context.js';
import { printJson } from '../redact.js';
import { EXIT, fields, out, relativeTime, style, symbols } from '../ui.js';

const STATUSES: readonly BankAccountStatus[] = [
  'ACTIVE',
  'SUSPENDED',
  'DELETED',
  'PENDING_VERIFICATION',
];

export interface AccountFlags extends GlobalFlags {
  limit?: string | undefined;
  status?: string | undefined;
}

/** `MB`, falling back to the display name when the bank relation is absent. */
function bankLabel(account: BankAccount): string {
  return account.bank?.code ?? account.bank?.shortName ?? '—';
}

function statusCell(status: BankAccountStatus): string {
  return status === 'ACTIVE' ? status : style.yellow(status);
}

export async function accountsCommand(
  subcommand: string | undefined,
  positional: string[],
  flags: AccountFlags,
): Promise<number> {
  const client = clientFromFlags(flags);

  switch (subcommand) {
    case 'list': {
      // No default status filter on purpose: a SUSPENDED account is exactly
      // what explains a transfer that never arrives ten minutes later.
      const status = enumArg(flags.status, 'status', STATUSES);
      const limit = intArg(flags.limit, 'limit');

      const page = await client.bankAccounts.list({
        ...(status === undefined ? {} : { status }),
        ...(limit === undefined ? {} : { limit }),
      });

      if (flags.json === true) {
        printJson(page);
        return EXIT.OK;
      }

      if (page.data.length === 0) {
        out(style.dim('No bank accounts on this token.'));
        out();
        out(
          style.dim(
            '  Add one at https://app.gpmpay.com/bank-accounts — transactions\n' +
              '  and webhooks are always scoped to a bank account.',
          ),
        );
        return EXIT.OK;
      }

      out(`${symbols.ok()} ${String(page.data.length)} bank account(s)`);
      out();

      for (const account of page.data) {
        // The id is printed bare — no styling, no truncation. Copy-pasting it
        // into `--account` is the entire reason this command exists, and ANSI
        // codes end up inside a double-click selection on some terminals.
        out(
          `  ${account.id}  ${bankLabel(account).padEnd(6)} ` +
            `${account.accountNumber.padEnd(16)} ` +
            `${account.ownerName.slice(0, 20).padEnd(22)} ` +
            statusCell(account.status),
        );
      }

      out();
      out(
        style.dim(
          '  Copy an id into: gpmpay simulate tx --account <id> --amount 50000 --content REF1',
        ),
      );
      return EXIT.OK;
    }

    case 'get': {
      const id = requireArg(positional[0], 'account-id');
      const account = await client.bankAccounts.retrieve(id);

      if (flags.json === true) {
        printJson(account);
        return EXIT.OK;
      }

      const bank = account.bank;
      out(
        fields([
          ['ID', account.id],
          [
            'Bank',
            bank
              ? `${bank.code} — ${bank.name} ${style.dim(`(BIN ${bank.bin})`)}`
              : style.dim('unknown'),
          ],
          ['Number', account.accountNumber],
          ['Holder', account.ownerName],
          ['Status', statusCell(account.status)],
          ['Expires', relativeTime(account.expiresAt)],
          ['Created', relativeTime(account.createdAt)],
        ]),
      );
      return EXIT.OK;
    }

    default:
      throw new GpmPayConfigError(
        `Unknown subcommand "accounts ${subcommand ?? ''}". Expected: list | get.`,
        'invalid_argument',
      );
  }
}
